import React, { useEffect, useState } from "react";
import { Mail, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatDateTime } from "@/lib/dateTime";

interface Schedule {
  id: string;
  report: string;
  label: string;
  frequency: string;
  weekday: number | null;
  hour: number;
  last_sent_at: string | null;
}
const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const hourLabel = (h: number) => `${String(h).padStart(2, "0")}:00`;
const when = (s: Pick<Schedule, "frequency" | "weekday" | "hour">) => `${s.frequency === "DAILY" ? "Every day" : s.frequency === "WEEKLY" ? `Every ${DAYS[s.weekday ?? 1]}` : "On the 1st of each month"} at ${hourLabel(s.hour)}`;
const field = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-1 focus:ring-primary";

/** Reports emailed to me on a schedule: what is set up, send one now, stop one, and add one */
export function ScheduleReportsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { success, error } = useToast();
  const [catalog, setCatalog] = useState<{ reports: { key: string; label: string }[] }>({ reports: [] });
  const [rows, setRows] = useState<Schedule[]>([]);
  const [form, setForm] = useState({ report: "PIPELINE", frequency: "WEEKLY", weekday: 1, hour: 8 });
  const [busy, setBusy] = useState("");

  const load = () =>
    api
      .get("/api/ReportSchedule/List")
      .then((r) => setRows(unwrapArray<Schedule>(r)))
      .catch(() => undefined);

  useEffect(() => {
    if (!open) return;
    void load();
    api
      .get("/api/ReportSchedule/Catalog")
      .then((r) => {
        const c = unwrapObject<{ reports: { key: string; label: string }[] }>(r);
        setCatalog(c);
        setForm((f) => ({ ...f, report: c.reports[0]?.key || f.report }));
      })
      .catch(() => undefined);
  }, [open]);

  const save = async () => {
    setBusy("save");
    try {
      await api.post("/api/ReportSchedule/Save", { report: form.report, frequency: form.frequency, weekday: form.frequency === "WEEKLY" ? form.weekday : null, hour: form.hour });
      success("Scheduled", "It will be emailed to you at that time.");
      await load();
    } catch (e: any) {
      error("Couldn't schedule it", e?.message || "Try again");
    } finally {
      setBusy("");
    }
  };
  const remove = async (id: string) => {
    setBusy(id);
    try {
      await api.post("/api/ReportSchedule/Delete", { id });
      setRows((cur) => cur.filter((r) => r.id !== id));
    } catch (e: any) {
      error("Couldn't stop it", e?.message || "Try again");
    } finally {
      setBusy("");
    }
  };
  const sendNow = async (id: string) => {
    setBusy(`now-${id}`);
    try {
      const r = unwrapObject<{ sent: boolean; message: string }>(await api.post(`/api/ReportSchedule/SendNow/${id}`));
      if (r.sent) success("Sent", "Check your inbox.");
      else error("Not sent", r.message);
      await load();
    } catch (e: any) {
      error("Couldn't send it", e?.message || "Try again");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Email me reports</DialogTitle>
          <DialogDescription>Pick a report and when it should reach you. It is built with your own access when it is sent, and goes to your own email address.</DialogDescription>
        </DialogHeader>

        <section className="space-y-2">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing is scheduled yet.</p>}
          {rows.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{s.label}</div>
                <div className="text-xs text-muted-foreground">
                  {when(s)}
                  {s.last_sent_at ? ` · last sent ${formatDateTime(s.last_sent_at)}` : ""}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="sm" variant="outline" disabled={busy === `now-${s.id}`} onClick={() => sendNow(s.id)}>
                  <Mail className="mr-1.5 size-3.5" /> Send now
                </Button>
                <Button size="icon-sm" variant="ghost" aria-label={`Stop ${s.label}`} disabled={busy === s.id} onClick={() => remove(s.id)}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </section>

        <section className="space-y-3 border-t border-border pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Add or change a schedule</h3>
          <label className="block space-y-1 text-xs font-medium text-muted-foreground">
            Report
            <select className={field} value={form.report} onChange={(e) => setForm({ ...form, report: e.target.value })}>
              {catalog.reports.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              How often
              <select className={field} value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                <option value="DAILY">Every day</option>
                <option value="WEEKLY">Every week</option>
                <option value="MONTHLY">Every month</option>
              </select>
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              Day
              <select className={field} disabled={form.frequency !== "WEEKLY"} value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}>
                {DAYS.map((d, i) => (
                  <option key={d} value={i}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-xs font-medium text-muted-foreground">
              Time
              <select className={field} value={form.hour} onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {hourLabel(h)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-[11px] text-muted-foreground">Times are in the company's time zone. Monthly reports go out on the 1st.</p>
          <div className="flex justify-end">
            <Button disabled={busy === "save"} onClick={save}>
              {busy === "save" ? "Saving…" : "Save schedule"}
            </Button>
          </div>
        </section>
      </DialogContent>
    </Dialog>
  );
}
